import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { ContactReferenceType } from '@/api/address-book/types';
import { _getBffContacts } from '@/api/address-book-bff/queries';
import useDebounce from '@/global-hooks/use-debounce';

import type { ContactSuggestion } from './contact-autocomplete';

const SUGGESTION_LIMIT = 5;
const SEARCH_DEBOUNCE_MS = 300;

const MIN_SEARCH_LENGTH = 2;

interface SuggestionsResult {
  suggestions: ContactSuggestion[];
  isLoading: boolean;
}

function useSuggestions(
  term: string | undefined,
  referenceType: (typeof ContactReferenceType)[keyof typeof ContactReferenceType] | undefined,
  toSupportText: (_contact: { email: string | null }) => string | null,
): SuggestionsResult {
  const search = useDebounce((term ?? '').trim(), SEARCH_DEBOUNCE_MS);
  const enabled = search.length >= MIN_SEARCH_LENGTH;

  const { data, isFetching } = useQuery({
    ..._getBffContacts({ search, limit: SUGGESTION_LIMIT, referenceType }),
    enabled,
  });

  const suggestions = useMemo<ContactSuggestion[]>(
    () =>
      (data?.contacts ?? []).map((contact) => ({
        id: contact.id,
        label: contact.name ?? '',
        supportText: toSupportText(contact),
        iconUrl: contact.iconUrl,
      })),
    // `toSupportText` is a module-level constant at both call sites, so it never invalidates this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data],
  );

  return { suggestions, isLoading: enabled && isFetching };
}

const emailOf = (contact: { email: string | null }) => contact.email;
const noSupportText = () => null;

export const useContactSuggestions = (term: string | undefined): SuggestionsResult =>
  useSuggestions(term, undefined, emailOf);

export const useOrganizationSuggestions = (term: string | undefined): SuggestionsResult =>
  useSuggestions(term, ContactReferenceType.Organization, noSupportText);
